import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Briefcase, Upload, Download, FileText, AlertTriangle, Users, Plus, RefreshCw } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { humanLabel } from "../i18n/ctLabels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/contract-twin`;

export default function DueDiligence() {
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState(null);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const fileRef = useRef(null);

  const loadProjects = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/dd-projects`, { withCredentials: true });
      setProjects(r.data);
    } catch { /* noop */ }
  }, []);

  const loadSummary = useCallback(async (pid) => {
    setSummary(null);
    try {
      const r = await axios.get(`${API}/dd-projects/${pid}/summary`, { withCredentials: true });
      setSummary(r.data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);
  useEffect(() => { if (selected) loadSummary(selected); }, [selected, loadSummary]);

  const create = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const r = await axios.post(`${API}/dd-projects`, { name: newName.trim() }, { withCredentials: true });
      setNewName("");
      await loadProjects();
      setSelected(r.data.id);
    } catch { /* noop */ } finally { setCreating(false); }
  };

  const uploadFiles = async (files) => {
    if (!selected || !files?.length) return;
    setUploading(true);
    for (const f of files) {
      const form = new FormData();
      form.append("file", f);
      try {
        await axios.post(`${API}/dd-projects/${selected}/upload`, form, {
          withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
        });
      } catch { /* skip */ }
    }
    setUploading(false);
    loadSummary(selected);
    loadProjects();
  };

  const downloadReport = async () => {
    if (!selected) return;
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/dd-projects/${selected}/report.pdf`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `dd-${summary?.project?.name || "report"}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { /* noop */ } finally { setDownloading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0C10] text-white p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 flex items-center justify-center bg-[#7C3AED]/12" style={{ borderRadius: "2px" }}>
          <Briefcase className="w-5 h-5 text-[#7C3AED]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>{t("ct.dd.title")}</h1>
          <p className="text-xs text-[#A0AEC0] tracking-wide">
            {t("ct.dd.empty").split(".")[0]}.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-1 space-y-3">
          {/* New project */}
          <div className="bg-[#0D0F12] border border-white/[0.04] p-4" style={{ borderRadius: "2px" }}>
            <p className="text-[10px] tracking-[0.22em] uppercase text-[#7C3AED] font-bold mb-2">{t("ct.dd.new_project")}</p>
            <input
              type="text" value={newName} onChange={e => setNewName(e.target.value)}
              placeholder="es. Acquisizione Delta - Q3 2026"
              className="w-full bg-[#0A0C10] border border-white/[0.06] px-3 py-1.5 text-xs text-white focus:outline-none focus:border-[#7C3AED]"
              style={{ borderRadius: "2px" }}
              data-testid="dd-newproject-input"
            />
            <button onClick={create} disabled={creating || !newName.trim()}
              className="mt-2 w-full bg-[#7C3AED] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest hover:bg-[#B39BFF] disabled:opacity-40 flex items-center justify-center gap-1"
              style={{ borderRadius: "2px" }}
              data-testid="dd-newproject-create"
            >
              <Plus className="w-3 h-3" /> {creating ? "…" : t("ct.dd.new_project")}
            </button>
          </div>

          {/* Projects list */}
          <div className="bg-[#0D0F12] border border-white/[0.04]" style={{ borderRadius: "2px" }}>
            <div className="flex items-center px-4 py-2.5">
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold">{t("ct.dd.projects")}</p>
              <button onClick={loadProjects} className="ml-auto text-[10px] text-[#A0AEC0] hover:text-white"><RefreshCw className="w-3 h-3" /></button>
            </div>
            {projects.length === 0 ? (
              <p className="text-[11px] text-[#6B7280] py-6 px-4 text-center">{t("ct.dd.empty")}</p>
            ) : (
              <ul className="divide-y divide-white/[0.03]">
                {projects.map(p => (
                  <li key={p.id}>
                    <button
                      onClick={() => setSelected(p.id)}
                      className={`w-full text-left px-4 py-2.5 hover:bg-white/[0.03] ${selected === p.id ? "bg-[#7C3AED]/10 border-l-2 border-[#7C3AED]" : ""}`}
                      data-testid={`dd-project-${p.id}`}
                    >
                      <p className="text-sm text-white font-medium truncate">{p.name}</p>
                      <p className="text-[10px] text-[#A0AEC0]">{p.contracts_count} contratti · {p.created_at?.slice(0, 10)}</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="md:col-span-2 space-y-3">
          {!selected ? (
            <div className="bg-[#0D0F12] border border-white/[0.04] p-8 text-center" style={{ borderRadius: "2px" }}>
              <Briefcase className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
              <p className="text-sm text-[#A0AEC0]">Seleziona un progetto Due Diligence per iniziare</p>
            </div>
          ) : (
            <>
              {/* Uploader */}
              <div
                onDrop={e => { e.preventDefault(); uploadFiles(e.dataTransfer.files); }}
                onDragOver={e => e.preventDefault()}
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${uploading ? "opacity-60" : "hover:bg-white/[0.02]"}`}
                style={{ borderColor: "#7C3AED55", borderRadius: "2px" }}
                data-testid="dd-uploader"
              >
                <input ref={fileRef} type="file" accept="application/pdf" multiple hidden
                  onChange={e => { if (e.target.files?.length) uploadFiles(e.target.files); e.target.value = ""; }} />
                <Upload className="w-8 h-8 text-[#7C3AED] mx-auto mb-2" />
                <p className="text-sm text-white">{uploading ? "Caricamento…" : t("ct.dd.upload_bulk")}</p>
                <p className="text-[10px] text-[#A0AEC0] mt-1">Portafoglio isolato dal Contract Twin operativo</p>
              </div>

              {/* Summary */}
              {summary && (
                <div className="bg-[#0D0F12] border border-white/[0.04] p-4" style={{ borderRadius: "2px" }} data-testid="dd-summary">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-[10px] tracking-[0.22em] uppercase text-[#7C3AED] font-bold">{t("ct.dd.summary")}</p>
                      <p className="text-lg font-bold text-white mt-1">{summary.project.name}</p>
                    </div>
                    <button onClick={downloadReport} disabled={downloading}
                      className="bg-[#00E5FF] text-black px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest hover:bg-white disabled:opacity-40 flex items-center gap-1"
                      style={{ borderRadius: "2px" }}
                      data-testid="dd-download-report"
                    >
                      <Download className="w-3.5 h-3.5" /> {downloading ? t("ct.report.generating") : t("ct.dd.download_report")}
                    </button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                    <StatBlock label={t("ct.dd.contracts_count")} value={summary.totals.contracts} color="#00E5FF" />
                    <StatBlock label="Clausole" value={summary.totals.clauses} color="#7C3AED" />
                    <StatBlock label="Valore totale" value={`€${Number(summary.totals.total_value_eur).toLocaleString("it-IT")}`} color="#00E676" />
                    <StatBlock label={t("ct.dd.low_conf")} value={summary.totals.low_confidence_clauses} color="#FFB800" />
                  </div>

                  <p className="text-[10px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold mb-2">{t("ct.dd.top_cp")}</p>
                  {summary.top_counterparties.length === 0 ? (
                    <p className="text-[11px] text-[#6B7280] italic">Nessuna controparte identificata</p>
                  ) : (
                    <ul className="text-xs space-y-1">
                      {summary.top_counterparties.map(cp => (
                        <li key={cp.name} className="flex items-center justify-between border-b border-white/[0.03] py-1.5">
                          <span className="text-white flex items-center gap-2">
                            <Users className="w-3 h-3 text-[#A0AEC0]" /> {cp.name}
                            <span className="text-[10px] text-[#A0AEC0]">· {cp.contracts} contratti</span>
                          </span>
                          <span className="tabular-nums text-[#00E5FF] font-bold">€{Number(cp.value).toLocaleString("it-IT")}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <p className="text-[10px] tracking-[0.22em] uppercase text-[#FFB800] font-bold mt-4 mb-2">Variabili esposte</p>
                  <div className="flex flex-wrap gap-1.5">
                    {summary.variable_summary.map(v => (
                      <span key={v.variable} className="text-[10px] bg-[#FFB800]/15 text-[#FFB800] px-1.5 py-0.5" style={{ borderRadius: "2px" }}>
                        {humanLabel(t, "variable", v.variable)} · {v.contracts}c · €{Number(v.exposure_eur).toLocaleString("it-IT")}
                      </span>
                    ))}
                    {summary.variable_summary.length === 0 && <span className="text-[11px] text-[#6B7280] italic">Nessuna</span>}
                  </div>

                  {summary.totals.low_confidence_clauses > 0 && (
                    <div className="mt-4 flex items-start gap-2 bg-[#FFB800]/8 border border-[#FFB800]/30 p-2" style={{ borderRadius: "2px" }}>
                      <AlertTriangle className="w-4 h-4 text-[#FFB800] flex-shrink-0 mt-0.5" />
                      <p className="text-[11px] text-[#FFB800]">
                        {summary.totals.low_confidence_clauses} clausole hanno confidence &lt; 70%: revisiona manualmente prima di fidarti dei numeri aggregati.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatBlock({ label, value, color }) {
  return (
    <div className="bg-[#0A0C10] border border-white/[0.04] p-2" style={{ borderRadius: "2px" }}>
      <p className="text-[9px] uppercase tracking-widest text-[#A0AEC0]">{label}</p>
      <p className="text-lg font-bold tabular-nums mt-0.5" style={{ color, fontFamily: "Chivo, sans-serif" }}>{value}</p>
    </div>
  );
}
